// import node module libraries
// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import { Link } from 'react-router-dom';
import { Col, Form, Row, Table } from 'react-bootstrap';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';

// import custom components
// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
// import utility file
import EmpruntService from '../../services/EmpruntService';
import LivreService from '../../services/LivreService';
import EleveService from '../../services/EleveService';

const EmpruntsListItems = () => {
	const [originalEmprunts, setOriginalEmprunts] = useState([]);
	const [emprunts, setEmprunts] = useState([]);
	const [livre, setLivre] = useState([]); // Add this line
	const [livreOptions, setLivreOptions] = useState([]);
	const [eleve, setEleve] = useState([]); // Add this line
	const [eleveOptions, setEleveOptions] = useState([]);

	useEffect(() => {
		const fetchLivreOptions = async () => {
			try {
				const livres = await LivreService.getLivreOptions();
				setLivreOptions(livres);
			} catch (error) {
				console.error('Error fetching class options', error);
			}
		};

		fetchLivreOptions();
	}, []);

	const CLASSE_CHOICES = livreOptions;

	useEffect(() => {
		Promise.all([
			EmpruntService.getAllEmprunts(),
			LivreService.getAllLivres()
		]).then(([empruntsData, livreData]) => {
			setOriginalEmprunts(empruntsData);
			setEmprunts(empruntsData);
			setLivre(livreData);
		});
	}, []);

	useEffect(() => {
		const fetchEleveOptions = async () => {
			try {
				const eleves = await EleveService.getEleveOptions();
				setEleveOptions(eleves);
			} catch (error) {
				console.error('Error fetching eleve options', error);
			}
		};

		fetchEleveOptions();
	}, []);

	const Eleve_CHOICES = eleveOptions;

	useEffect(() => {
		Promise.all([
			EmpruntService.getAllEmprunts(),
			EleveService.getAllEleves()
		]).then(([EmpruntsData, eleveData]) => {
			setOriginalEmprunts(EmpruntsData);
			setEmprunts(EmpruntsData);
			setEleve(eleveData);
		});
	}, []);
	React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to=""
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));
	const formatValue = (value, columnId) => {
		if (typeof value === 'string') {
			if (columnId === 'eleve' && value) {
				return value.toUpperCase();
			} else if (columnId === 'livre' || (columnId === 'livre' && value)) {
				return (
					value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
				);
			} else {
				return value;
			}
		} else if (columnId === 'livre') {
			return value && value.titre ? value.titre.toUpperCase() : ''; // Return the titre property
		} else if (columnId === 'eleve') {
			return value && value.nom ? value.nom.toUpperCase() : ''; // Return the titre property
		}
		return value;
	};

	const formatStatutEmprunt = (value) => {
		// Remplacez les underscores par des espaces et mettez la première lettre en majuscule
		return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	};

	const getLivreById = (livreId) => {
		return livre.find((item) => item.id === livreId) ?? {};
	};
	const getEleveById = (eleveId) => {
		return eleve.find((item) => item.id === eleveId) ?? {};
	};

	const getInitials = (student) => {
		const firstName = student.prenom
			? student.prenom.charAt(0).toUpperCase()
			: '';
		const lastName = student.nom ? student.nom.charAt(0).toUpperCase() : '';
		return `${lastName} ${firstName}`;
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'livre',
				Header: 'Livre',
				Cell: ({ value, row }) => {
					return (
						<div className="d-flex align-items-center">
							{value ? (
								<Link to={`/services/bibliotheque/emprunts/${row.original.id}`}>
									<h5 className="mb-0 ms-2">{formatValue(value, 'livre')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},
			{
				accessor: 'eleve',
				Header: 'Eleve',
				Cell: ({ value }) => <div>{formatValue(value, 'eleve')}</div>
			},
			{
				accessor: 'date_emprunt',
				Header: "Date d'emprunt",
				Cell: ({ value }) => <div>{formatValue(value, "Date d'emprunt")}</div>
			},
			{
				accessor: 'date_retour_prevue',
				Header: 'Date de retour prevue',
				Cell: ({ value }) => (
					<div>{formatValue(value, 'Date de retour prevue')}</div>
				)
			},
			{
				accessor: 'date_retour_effective',
				Header: 'Date de retour effective',
				Cell: ({ value }) => <div>{formatStatutEmprunt(value)}</div>
			}
		],
		[]
	);
	const data = useMemo(() => {
		return emprunts.map((emprunt) => ({
			...emprunt,
			livre: getLivreById(emprunt.livre),
			eleve: getEleveById(emprunt.eleve)
		}));
	}, [emprunts]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;
		if (filterTerm !== '') {
			const newEmpruntsList = originalEmprunts.filter((emprunt) => {
				return emprunt.livre === Number(filterTerm);
			});
			setEmprunts(newEmpruntsList);
		} else {
			setEmprunts(originalEmprunts);
		}
	};

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xxl={3} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={setGlobalFilter}
							placeholder="Rechercher un élève"
						/>
					</Col>
					<Col xxl={3} lg={4} md={6} xs={12}>
						{/* records filtering options */}
						<Form.Control
							as={FormSelect}
							placeholder="Filtrer par livre"
							options={CLASSE_CHOICES}
							onChange={getFilterTerm}
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="table text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default EmpruntsListItems;
